import { ReactNode } from "react";
import { useTutorial, TutorialStep } from "@/hooks/useTutorial";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TutorialHighlightProps {
  step: TutorialStep;
  children: ReactNode;
  tooltipContent?: string;
  tooltipSide?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export function TutorialHighlight({ 
  step, 
  children, 
  tooltipContent,
  tooltipSide = "top",
  className = "" 
}: TutorialHighlightProps) {
  const { currentStep, progress } = useTutorial();
  
  // Don't highlight if tutorial is completed/dismissed or this isn't the current step
  const isActive = currentStep === step && !progress?.tutorial_completed && !progress?.tutorial_dismissed;

  if (!isActive) {
    return <>{children}</>;
  }

  const highlightedContent = (
    <div 
      className={`relative ${className}`}
    >
      {/* Pulsing ring effect */}
      <div className="absolute -inset-1 rounded-lg bg-primary/20 animate-pulse" />
      <div className="absolute -inset-0.5 rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background" />
      <div className="relative">
        {children}
      </div>
    </div>
  );

  if (tooltipContent) {
    return (
      <Tooltip open>
        <TooltipTrigger asChild>
          {highlightedContent}
        </TooltipTrigger>
        <TooltipContent 
          side={tooltipSide}
          className="bg-primary text-primary-foreground max-w-xs"
        >
          <p className="text-sm">{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return highlightedContent;
}
